import React, { FC, useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Grid, IconButton } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { FormProvider, useForm } from 'react-hook-form'
import { endOfDay, startOfDay } from 'date-fns'
import { capitalize } from 'lodash'
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import PrintIcon from '@mui/icons-material/Print'
import {
  CommissionsStatusEnum,
  GetHistoryRequestDto,
  HistoryEntity,
  TradeOrderStatusEnum,
  TransactionOwnerEnum,
  getUniqueDashboardStatuses,
  getOriginalStatusesByDashboardLabel,
} from '@sparling/financial-common'
import { DataPicker } from '../../../../../components/Dashboard/DatePicker'
import { InputSelect } from '../../../../../components/Dashboard/InputSelect'
import { InputField } from '../../../../../components/Dashboard/InputField'
import { icons } from '../../../../../constants/icons'
import {
  historyFiltersSelector,
  historyIsLoadingSelector,
  historyItemsSelector,
  historyMetaSelector,
} from '../../../../../store/selectors'
import { getHistoryRequest, setHistoryFilters } from '../../../../../store/actions'
import { IDataItem, TableComponent } from '../../../../../components/Dashboard/Table'
import { getColumnsByHistoryEntity } from './MyHistoryList'
import { createEnum } from '../../../../../utils/createEnum'

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
  },
  formWrap: {
    display: 'flex',
    width: '100%',
    height: '100%',
    flexDirection: 'column',
    paddingTop: theme.spacing(4),
  },
  sectionWrap: {
    width: '100%',
    height: '100%',
    marginTop: theme.spacing(4),
    padding: theme.spacing(0, 4, 2, 4),
    background: theme.palette.background.paper,
  },
  iconButton: {
    '& .MuiSvgIcon-root': {
      transition: 'color 120ms ease-in',
      color: 'rgba(255, 255, 255, 0.8)',
    },
    '&:hover': {
      '& .MuiSvgIcon-root': {
        color: 'rgba(255, 255, 255, 0.9)',
      },
    },
  },
  '@global': {
    '@media print': {
      'body *': {
        visibility: 'hidden',
      },
      '#printableArea, #printableArea *': {
        visibility: 'visible',
      },
      '#printableArea': {
        position: 'absolute',
        left: 0,
        top: 0,
      },
    },
  },
}))

export const MyHistory: FC = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const data = useSelector(historyItemsSelector)
  const meta = useSelector(historyMetaSelector)
  const filters = useSelector(historyFiltersSelector)
  const loading = useSelector(historyIsLoadingSelector)
  const formMethods = useForm<GetHistoryRequestDto>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    defaultValues: filters,
  })
  const { watch, getValues, setValue } = formMethods
  const type = getValues().type as HistoryEntity
  const columns = getColumnsByHistoryEntity(type || HistoryEntity.ALL)

  const statusOptions = useMemo(() => {
    switch (type) {
      case HistoryEntity.TRANSACTION:
        return createEnum(getUniqueDashboardStatuses(TransactionOwnerEnum.FIREBLOCKS), (label) => label)
      case HistoryEntity.COMMISSION:
        return createEnum(CommissionsStatusEnum, capitalize)
      case HistoryEntity.TRADE:
        return createEnum(TradeOrderStatusEnum, capitalize)
      default:
        return []
    }
  }, [type])

  useEffect(() => {
    dispatch(getHistoryRequest({ limit: 10, page: 1, type: HistoryEntity.ALL }))
  }, [dispatch])

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'type') {
        if (value.status !== undefined && value.status !== '') {
          setValue('status', undefined)
        }

        const requestData = { ...value, status: undefined } as GetHistoryRequestDto

        dispatch(setHistoryFilters(requestData))
        dispatch(getHistoryRequest(requestData))
        return
      }

      if (name === 'status' && (value.status === '' || value.status === undefined)) {
        return
      }

      // For transactions, convert dashboard status to comma-separated original statuses
      let requestData = value as GetHistoryRequestDto

      if (value.type === HistoryEntity.TRANSACTION && value.status) {
        const originalStatuses = getOriginalStatusesByDashboardLabel(
          TransactionOwnerEnum.FIREBLOCKS,
          value.status as string,
        )

        if (originalStatuses.length > 0) {
          requestData = { ...value, status: originalStatuses.join(',') } as GetHistoryRequestDto
        }
      }

      dispatch(setHistoryFilters(requestData))
      dispatch(getHistoryRequest(requestData))
    })

    return () => subscription.unsubscribe()
  }, [watch, dispatch, setValue])

  const loadDataHandle = useCallback(
    (pageNumber: string | number) => {
      dispatch(getHistoryRequest({ ...filters, page: Number(pageNumber) }))
    },
    [dispatch, filters],
  )

  const exportHandle = useCallback(() => {
    const header = columns.filter((c) => c.id !== 'actions').map((c) => c.label ?? String(c.id))
    const ArrayData = data.map((item) =>
      columns
        .filter((c) => c.id !== 'actions')
        .map((column) => {
          if (column.format) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            return column.format(item[column.id] as unknown as string, undefined, undefined, undefined, item) || ''
          }
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          return (item[column.id] as unknown as string) || ''
        }),
    )

    const csvContent = [header.join(','), ...ArrayData.map((row) => row.join(','))].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `history_export_${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }, [columns, data])

  const printHandle = useCallback(() => {
    window.print?.()
  }, [])

  return (
    <Box className={classes.container}>
      <Grid item xs={12} className={classes.formWrap}>
        <FormProvider {...formMethods}>
          <Grid item xs={12} className={classes.container}>
            <Grid item xs={8} display={'flex'} alignItems={'center'}>
              <Grid item>
                <DataPicker
                  placeholder={'Start Date'}
                  margin={'0 8px 0 0'}
                  dataPickerName={'startDate'}
                  required={false}
                  maxDate={getValues().endDate}
                  formatValue={startOfDay}
                />
              </Grid>
              <Grid item>
                <DataPicker
                  placeholder={'End Date'}
                  margin={'0 8px'}
                  dataPickerName={'endDate'}
                  required={false}
                  minDate={getValues().startDate}
                  formatValue={endOfDay}
                />
              </Grid>
              <Grid item>
                <InputSelect
                  placeholderDisabled={false}
                  inputName={'type'}
                  placeholder="Type"
                  width={160}
                  enums={createEnum(HistoryEntity, capitalize)}
                  ml={8}
                  mr={8}
                />
              </Grid>
              {type && type !== HistoryEntity.ALL && (
                <Grid item>
                  <InputSelect
                    placeholderDisabled={false}
                    inputName={'status'}
                    placeholder="Status"
                    width={160}
                    enums={statusOptions}
                    ml={8}
                    mr={8}
                  />
                </Grid>
              )}
              <Grid item>
                <InputField
                  inputName="search"
                  placeholder="Search"
                  inputType="text"
                  marginBottom={'0'}
                  fontSize={16}
                  ml={8}
                  mr={8}
                  borderColor={'rgba(255,255,255,0.6)'}
                  prefix={icons.SEARCH}
                />
              </Grid>
            </Grid>

            <Grid item xs={4} display={'flex'} justifyContent={'flex-end'} alignItems={'center'}>
              <IconButton className={classes.iconButton} onClick={exportHandle}>
                <CloudDownloadIcon color={'secondary'} />
              </IconButton>
              <IconButton className={classes.iconButton} onClick={printHandle}>
                <PrintIcon color={'secondary'} />
              </IconButton>
            </Grid>
          </Grid>

          <Grid item xs={12} className={classes.sectionWrap}>
            <TableComponent
              tableContainerId="printableArea"
              columns={columns}
              data={data as unknown as IDataItem[]}
              params={meta}
              loadData={loadDataHandle}
              loading={loading}
            />
          </Grid>
        </FormProvider>
      </Grid>
    </Box>
  )
}
